import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabase';
import SchoolSidebar from './SchoolSidebar';
import { SchoolMobileHeader } from './SchoolMobileHeader';
import './SchoolDashboard.css';
import './SchoolHomework.css';
import BranchSelector from './BranchSelector';
import { useBranchFilter } from '../../hooks/useBranchFilter';

function SchoolHomework() {
  const navigate = useNavigate();
  const [schoolData, setSchoolData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { selectedBranchId, handleBranchChange } = useBranchFilter();
  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedSection, setSelectedSection] = useState('');
  const [homeworkData, setHomeworkData] = useState([]);
  const [homeworkLoading, setHomeworkLoading] = useState(false);
  const [homeworkDate, setHomeworkDate] = useState(new Date().toISOString().split('T')[0]);
  const [filteredHomework, setFilteredHomework] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [subjectFilter, setSubjectFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [newHw, setNewHw] = useState({
    title: '',
    description: '',
    subject: '',
    teacherId: '',
    assignedDate: new Date().toISOString().split('T')[0],
    dueDate: new Date().toISOString().split('T')[0],
    isPublished: false,
    file: null,
    allowDownload: true
  });

  useEffect(() => {
    // Check if user is authenticated
    const isAuthenticated = localStorage.getItem('schoolAuthenticated');
    const storedSchoolData = localStorage.getItem('schoolData');
    
    if (!isAuthenticated || !storedSchoolData) {
      navigate('/schoollogin');
      return;
    }
    
    setSchoolData(JSON.parse(storedSchoolData));
    setLoading(false);
  }, [navigate]);

  useEffect(() => {
    if (schoolData) {
      fetchClasses();
      fetchTeachers();
    }
  }, [schoolData, selectedBranchId]);

  useEffect(() => {
    setSelectedClass('');
    setSelectedSection('');
    setHomeworkData([]);
    setFilteredHomework([]);
  }, [selectedBranchId]);

  useEffect(() => {
    if (selectedClass) {
      fetchSections();
    }
  }, [selectedClass]);

  useEffect(() => {
    if (selectedClass && selectedSection) {
      fetchHomework();
    }
  }, [selectedClass, selectedSection, selectedBranchId]);

  useEffect(() => {
    if (homeworkData.length > 0) {
      filterHomeworkByDate(homeworkData, homeworkDate);
    }
  }, [homeworkData, homeworkDate]);

  const fetchClasses = async () => {
    try {
      let query = supabase
        .from('classes')
        .select('*')
        .eq('school_id', schoolData.id)
        .order('class_name');

      if (selectedBranchId != null) {
        query = query.eq('branch_id', selectedBranchId);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching classes:', error);
        return;
      }

      setClasses(data || []);
    } catch (error) {
      console.error('Error fetching classes:', error);
    }
  };

  const fetchSections = async () => {
    try {
      // Get sections from class_schedules table for the selected class
      let query = supabase
        .from('class_schedules')
        .select('section_name')
        .eq('school_id', schoolData.id)
        .eq('class_id', selectedClass);

      if (selectedBranchId != null) {
        query = query.eq('branch_id', selectedBranchId);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching sections:', error);
        return;
      }

      // Get unique sections
      const uniqueSections = [...new Set(data.map(item => item.section_name))].sort();
      setSections(uniqueSections);
    } catch (error) {
      console.error('Error fetching sections:', error);
    }
  };

  const fetchTeachers = async () => {
    try {
      let query = supabase
        .from('teachers')
        .select('id, first_name, last_name')
        .eq('school_id', schoolData.id)
        .eq('is_active', true)
        .order('first_name');

      if (selectedBranchId != null) {
        query = query.eq('branch_id', selectedBranchId);
      }

      const { data, error } = await query;
      if (!error) setTeachers(data || []);
    } catch (err) {
      console.error('Error fetching teachers', err);
    }
  };

  const fetchHomework = async () => {
    if (!selectedClass || !selectedSection) return;

    try {
      setHomeworkLoading(true);
      console.log('Fetching homework for class:', selectedClass, 'section:', selectedSection);

      let query = supabase
        .from('homework')
        .select(`
          *,
          teachers(first_name, last_name)
        `)
        .eq('school_id', schoolData.id)
        .eq('class_id', selectedClass)
        .eq('section_name', selectedSection);

      if (selectedBranchId != null) {
        query = query.eq('branch_id', selectedBranchId);
      }

      const { data, error } = await query.order('assigned_date', { ascending: false });

      if (error) {
        console.error('Error fetching homework:', error);
        return;
      }

      console.log('Homework data:', data);
      setHomeworkData(data || []);
    } catch (error) {
      console.error('Error fetching homework:', error);
    } finally {
      setHomeworkLoading(false);
    }
  };

  const filterHomeworkByDate = (homework, selectedDate) => {
    if (!homework || homework.length === 0) {
      setFilteredHomework([]);
      return;
    }
    let filtered = homework.filter(hw => hw.assigned_date === selectedDate);
    if (subjectFilter !== 'all') filtered = filtered.filter(hw => (hw.subject || '') === subjectFilter);
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(hw =>
        (hw.title || '').toLowerCase().includes(term) ||
        (hw.description || '').toLowerCase().includes(term) ||
        (hw.subject || '').toLowerCase().includes(term)
      );
    }
    console.log(`Filtering homework for date ${selectedDate}:`, filtered);
    setFilteredHomework(filtered);
  };

  const navigateHomeworkDate = (direction) => {
    const currentDate = new Date(homeworkDate);
    const newDate = new Date(currentDate);
    
    if (direction === 'prev') {
      newDate.setDate(currentDate.getDate() - 1);
    } else if (direction === 'next') {
      newDate.setDate(currentDate.getDate() + 1);
    }
    
    const newDateString = newDate.toISOString().split('T')[0];
    setHomeworkDate(newDateString);
    filterHomeworkByDate(homeworkData, newDateString);
  };

  const handleClassChange = (e) => {
    setSelectedClass(e.target.value);
    setSelectedSection('');
    setHomeworkData([]);
    setFilteredHomework([]);
  };

  const handleSectionChange = (e) => {
    setSelectedSection(e.target.value);
    setHomeworkData([]);
    setFilteredHomework([]);
  };

  const handleOpenAdd = () => {
    setIsAddModalOpen(true);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setNewHw(prev => ({ ...prev, file }));
  };

  const handleCreateHomework = async (e) => {
    e.preventDefault();
    if (!selectedClass || !selectedSection) return alert('Select class and section first');
    if (!newHw.teacherId) return alert('Select teacher');
    try {
      let attachmentObj = null;
      if (newHw.file) {
        const ext = newHw.file.name.split('.').pop();
        const key = `homework-files/${schoolData.id}/${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`;
        const { error: upErr } = await supabase.storage.from('student-images').upload(key, newHw.file);
        if (upErr) {
          console.warn('Upload failed:', upErr.message);
        } else {
          const { data: { publicUrl } } = supabase.storage.from('student-images').getPublicUrl(key);
          attachmentObj = [{ name: newHw.file.name, url: publicUrl, size: newHw.file.size }];
        }
      }
      const payload = {
        school_id: schoolData.id,
        teacher_id: parseInt(newHw.teacherId),
        class_id: selectedClass,
        section_name: selectedSection,
        subject: newHw.subject || null,
        title: newHw.title || 'Homework',
        description: newHw.description || '',
        assigned_date: newHw.assignedDate,
        due_date: newHw.dueDate,
        is_published: newHw.isPublished,
        attachments: attachmentObj,
        allow_download: newHw.allowDownload
        , branch_id: selectedBranchId
      };
      const { error } = await supabase.from('homework').insert(payload);
      if (error) {
        alert('Failed to add homework: ' + error.message);
        return;
      }
      setIsAddModalOpen(false);
      setNewHw({
        title: '', description: '', subject: '', teacherId: '',
        assignedDate: new Date().toISOString().split('T')[0],
        dueDate: new Date().toISOString().split('T')[0], isPublished: false,
        file: null, allowDownload: true
      });
      await fetchHomework();
      alert('Homework added');
    } catch (err) {
      alert('Error adding homework');
    }
  };

  const handleLogout = async () => {
    try {
      // Sign out from Supabase
      await supabase.auth.signOut();
      
      // Set logout flag to prevent auto-login
      sessionStorage.setItem('schoolLoggedOut', 'true');
      
      // Clear all localStorage
      localStorage.removeItem('schoolAuthenticated');
      localStorage.removeItem('schoolData');
      localStorage.removeItem('supabaseAuthToken');
      localStorage.removeItem('schoolRememberEmail');
      localStorage.removeItem('schoolRememberPassword');
      
      // Redirect to school login page
      navigate('/schoollogin');
    } catch (error) {
      console.error('Logout error:', error);
      // Still clear localStorage and redirect even if there's an error
      sessionStorage.setItem('schoolLoggedOut', 'true');
      localStorage.removeItem('schoolAuthenticated');
      localStorage.removeItem('schoolData');
      localStorage.removeItem('supabaseAuthToken');
      navigate('/schoollogin');
    }
  };

  if (loading) {
    return (
      <div className="school-dashboard">
        <SchoolMobileHeader 
          schoolData={schoolData} 
          mobileMenuOpen={mobileMenuOpen} 
          setMobileMenuOpen={setMobileMenuOpen} 
          handleLogout={handleLogout} 
        />
        
        {/* Sidebar */}
        <SchoolSidebar schoolData={schoolData} onLogout={handleLogout} />
        <div className="main-content">
          <div className="school-homework">
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>Loading...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="school-dashboard">
      <SchoolMobileHeader 
        schoolData={schoolData} 
        mobileMenuOpen={mobileMenuOpen} 
        setMobileMenuOpen={setMobileMenuOpen} 
        handleLogout={handleLogout} 
      />
      
      {/* Sidebar */}
      <SchoolSidebar schoolData={schoolData} onLogout={handleLogout} />
      <div className="main-content">
      <div className="school-homework">
      <div className="homework-header">
        <h1>📝 Homework Management</h1>
        <p>View and manage homework assignments by class and section</p>
      </div>
      <div className="branch-filter-row">
        <BranchSelector
          schoolId={schoolData?.id}
          selectedBranchId={selectedBranchId}
          onBranchChange={handleBranchChange}
        />
        <span className="branch-context-text">
          {selectedBranchId
            ? 'Showing homework for the selected branch'
            : 'Showing homework across all branches'}
        </span>
      </div>

      {/* Class and Section Selection */}
      <div className="selection-container">
        <div className="selection-group">
          <label htmlFor="class-select">Select Class:</label>
          <select 
            id="class-select"
            value={selectedClass} 
            onChange={handleClassChange}
            className="selection-dropdown"
          >
            <option value="">Choose a class...</option>
            {classes.map(cls => (
              <option key={cls.id} value={cls.id}>
                {cls.class_name}
              </option>
            ))}
          </select>
        </div>

        <div className="selection-group">
          <label htmlFor="section-select">Select Section:</label>
          <select 
            id="section-select"
            value={selectedSection} 
            onChange={handleSectionChange}
            className="selection-dropdown"
            disabled={!selectedClass}
          >
            <option value="">Choose a section...</option>
            {sections.map(section => (
              <option key={section} value={section}>
                {section}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Date Navigation */}
      {selectedClass && selectedSection && (
        <div className="homework-date-navigation">
          <button 
            className="nav-btn" 
            onClick={() => navigateHomeworkDate('prev')}
            disabled={homeworkLoading}
          >
            ← Previous Day
          </button>
          
          <div className="current-date">
            <h3>
              {homeworkDate === new Date().toISOString().split('T')[0] ? 'Today' :
               new Date(homeworkDate).toLocaleDateString('en-US', { 
                 weekday: 'long', 
                 year: 'numeric', 
                 month: 'long', 
                 day: 'numeric' 
               })}
            </h3>
            <p className="date-info">
              {filteredHomework.length} homework assignment{filteredHomework.length !== 1 ? 's' : ''} found
            </p>
          </div>
          
          <button 
            className="nav-btn" 
            onClick={() => navigateHomeworkDate('next')}
            disabled={homeworkLoading}
          >
            Next Day →
          </button>
          <div className="homework-filters-inline">
            <select className="selection-dropdown" value={subjectFilter} onChange={(e)=>{ setSubjectFilter(e.target.value); filterHomeworkByDate(homeworkData, homeworkDate); }}>
              <option value="all">All Subjects</option>
              {[...new Set(homeworkData.map(h=>h.subject).filter(Boolean))].map((s,i)=>(<option key={i} value={s}>{s}</option>))}
            </select>
            <input className="selection-dropdown" type="text" placeholder="Search title/description/subject" value={searchTerm} onChange={(e)=>{ setSearchTerm(e.target.value); filterHomeworkByDate(homeworkData, homeworkDate); }} />
          </div>
        </div>
      )}

      {/* Homework Display */}
      {selectedClass && selectedSection && (
        <div className="homework-content">
          {homeworkLoading ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>Loading homework...</p>
            </div>
          ) : homeworkData.length === 0 ? (
            <div className="no-homework">
              <div className="no-homework-icon">📚</div>
              <h3>No Homework Found</h3>
              <p>No homework assignments found for the selected class and section.</p>
            </div>
          ) : filteredHomework.length === 0 ? (
            <div className="no-homework">
              <div className="no-homework-icon">📅</div>
              <h3>No Homework for This Date</h3>
              <p>No homework assignments found for {homeworkDate === new Date().toISOString().split('T')[0] ? 'today' : 'this date'}.</p>
              <p>Use the navigation arrows to check other dates.</p>
            </div>
          ) : (
            <div className="homework-grid">
              {filteredHomework.map(homework => (
                <div key={homework.id} className="homework-card">
                  <div className="homework-header-card">
                    <h4>{homework.title}</h4>
                    <div className="homework-meta">
                      <span className="homework-subject">📖 {homework.subject}</span>
                      <span className="homework-teacher">👨‍🏫 {homework.teachers?.first_name} {homework.teachers?.last_name}</span>
                    </div>
                  </div>
                  
                  <div className="homework-details">
                    <div className="homework-description">
                      <p>{homework.description}</p>
                    </div>
                    
                    <div className="homework-dates">
                      <div className="assigned-date">
                        Assigned: {new Date(homework.assigned_date).toLocaleDateString()}
                      </div>
                      <div className="due-date">
                        <strong>Due:</strong> {new Date(homework.due_date).toLocaleDateString()}
                      </div>
                    </div>
                    {homework.attachments && Array.isArray(homework.attachments) && homework.attachments.length > 0 && (
                      <div className="homework-attachments">
                        {homework.attachments.map((f,idx)=> (
                          <a key={idx} href={f.url} download target="_blank" rel="noreferrer" className="download-link">⬇️ Download: {f.name}</a>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {!selectedClass && (
        <div className="selection-prompt">
          <div className="prompt-icon">📚</div>
          <h3>Select Class and Section</h3>
          <p>Choose a class and section from the dropdowns above to view homework assignments.</p>
        </div>
      )}

      {/* Add Homework Modal */}
      {isAddModalOpen && (
        <div className="popup-overlay">
          <div className="popup-content" style={{maxWidth:'720px'}}>
            <div className="popup-header">
              <h3>Add Homework</h3>
              <button className="close-btn" onClick={()=> setIsAddModalOpen(false)}>×</button>
            </div>
            <form onSubmit={handleCreateHomework} className="edit-student-form">
              <div className="form-section">
                <div className="form-row">
                  <div className="form-group">
                    <label>Teacher *</label>
                    <select value={newHw.teacherId} onChange={(e)=> setNewHw(prev=>({...prev, teacherId: e.target.value}))}>
                      <option value="">Select Teacher</option>
                      {teachers.map(t=> (<option key={t.id} value={t.id}>{t.first_name} {t.last_name}</option>))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Subject (optional)</label>
                    <input type="text" value={newHw.subject} onChange={(e)=> setNewHw(prev=>({...prev, subject: e.target.value}))} placeholder="e.g. Mathematics" />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Title</label>
                    <input type="text" value={newHw.title} onChange={(e)=> setNewHw(prev=>({...prev, title: e.target.value}))} />
                  </div>
                  <div className="form-group">
                    <label>Attachment (optional)</label>
                    <input type="file" onChange={handleFileChange} />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Assigned Date</label>
                    <input type="date" value={newHw.assignedDate} onChange={(e)=> setNewHw(prev=>({...prev, assignedDate: e.target.value}))} />
                  </div>
                  <div className="form-group">
                    <label>Due Date</label>
                    <input type="date" value={newHw.dueDate} onChange={(e)=> setNewHw(prev=>({...prev, dueDate: e.target.value}))} />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group" style={{flex:1}}>
                    <label>Description / Instructions</label>
                    <textarea rows="4" value={newHw.description} onChange={(e)=> setNewHw(prev=>({...prev, description: e.target.value}))} placeholder="Write homework details here..."></textarea>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>
                      <input type="checkbox" checked={newHw.isPublished} onChange={(e)=> setNewHw(prev=>({...prev, isPublished: e.target.checked}))} /> Publish (visible to Students/Parents)
                    </label>
                  </div>
                  <div className="form-group">
                    <label>
                      <input type="checkbox" checked={newHw.allowDownload} onChange={(e)=> setNewHw(prev=>({...prev, allowDownload: e.target.checked}))} /> Downloadable / Printable
                    </label>
                  </div>
                </div>
              </div>

              <div className="form-actions">
                <button type="button" className="cancel-btn" onClick={()=> setIsAddModalOpen(false)}>Cancel</button>
                <button type="submit" className="submit-btn">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}
        </div>
      </div>
    </div>
  );
}

export default SchoolHomework;
